import React, { useState } from 'react';
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import API_BASE_URL from '../config';

const ContactForm = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    category: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: '',
          category: '',
          message: ''
        });
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        category: '',
        message: ''
      });
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Modal 
    className='contact-us'
      show={open} 
      onHide={handleClose}
      size="lg"
      centered
    >
      <Modal.Header 
        closeButton
        style={{
          background: 'linear-gradient(135deg, #fb8b24 0%, #f39c12 100%)',
          color: 'white',
          borderBottom: 'none'
        }}
      >
        <Modal.Title className="fw-bold d-flex align-items-center gap-2">
          <span style={{ fontSize: '1.2rem' }}>💬</span>
          Contact Innovatix Support
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        <div className="text-center mb-4">
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Get in touch with our Innovatix team. We're here to help you operate 
            <span className="fw-bold" style={{ color: '#fb8b24' }}> faster, better & smarter</span>.
          </p>
        </div>

        {success && (
          <Alert variant="success" className="mb-3">
            Thank you! Your message has been sent successfully. We'll get back to you soon.
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ color: '#36454f', fontWeight: '600' }}>Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                  style={{ borderColor: '#ccdaf6', borderRadius: '6px' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ color: '#36454f', fontWeight: '600' }}>Email <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                  style={{ borderColor: '#ccdaf6', borderRadius: '6px' }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ color: '#36454f', fontWeight: '600' }}>Company</Form.Label>
                <Form.Control
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your company name"
                  style={{ borderColor: '#ccdaf6', borderRadius: '6px' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ color: '#36454f', fontWeight: '600' }}>Category <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  style={{ borderColor: '#ccdaf6', borderRadius: '6px' }}
                >
                  <option value="">Select a category</option>
                  <option value="general">General Inquiry</option>
                  <option value="technical">Technical Support</option>
                  <option value="billing">Billing</option>
                  <option value="feature">Feature Request</option>
                  <option value="bug">Bug Report</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label style={{ color: '#36454f', fontWeight: '600' }}>Subject <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder="Brief description of your inquiry"
              style={{ borderColor: '#ccdaf6', borderRadius: '6px' }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ color: '#36454f', fontWeight: '600' }}>Message <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Please provide details about your inquiry..."
              style={{ borderColor: '#ccdaf6', borderRadius: '6px' }}
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button 
              variant="outline-secondary" 
              onClick={handleClose}
              disabled={loading}
              style={{ borderColor: '#787e89', color: '#787e89' }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary"
              disabled={loading}
              style={{ backgroundColor: '#fb8b24', borderColor: '#fb8b24' }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ContactForm;